import React, { useState } from 'react';
import { Loader2, RefreshCw, Check, AlertCircle } from 'lucide-react';

interface OAuth2Config {
  grantType: 'client_credentials' | 'password' | 'authorization_code';
  accessTokenUrl: string;
  clientId: string;
  clientSecret?: string;
  scope?: string;
  username?: string;
  password?: string;
  accessToken?: string;
  tokenExpiresAt?: number;
}

interface OAuth2AuthProps {
  config: OAuth2Config;
  onChange: (config: OAuth2Config) => void;
}

export function OAuth2Auth({ config, onChange }: OAuth2AuthProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleChange = (updates: Partial<OAuth2Config>) => {
    onChange({ ...config, ...updates });
  };

  const handleGetToken = async () => {
    if (!config.accessTokenUrl || !config.clientId) {
      setError('Access Token URL and Client ID are required');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const params = new URLSearchParams();
      params.append('grant_type', config.grantType);
      params.append('client_id', config.clientId);

      if (config.clientSecret) {
        params.append('client_secret', config.clientSecret);
      }

      if (config.scope) {
        params.append('scope', config.scope);
      }

      if (config.grantType === 'password') {
        if (!config.username || !config.password) {
          setError('Username and password are required for password grant');
          return;
        }
        params.append('username', config.username);
        params.append('password', config.password);
      }

      const response = await fetch(config.accessTokenUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: params.toString(),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error_description || errorData.error || `HTTP ${response.status}`);
      }

      const data = await response.json();

      handleChange({
        accessToken: data.access_token,
        tokenExpiresAt: data.expires_in
          ? Date.now() + data.expires_in * 1000
          : undefined,
      });
    } catch (err) {
      console.error('Failed to get OAuth2 token:', err);
      setError(err instanceof Error ? err.message : 'Failed to get token');
    } finally {
      setIsLoading(false);
    }
  };

  const isTokenExpired = config.tokenExpiresAt && Date.now() > config.tokenExpiresAt;
  const hasValidToken = config.accessToken && !isTokenExpired;

  return (
    <div className="space-y-4">
      {/* Grant Type */}
      <div>
        <label className="block text-sm font-medium mb-1">Grant Type</label>
        <select
          value={config.grantType}
          onChange={(e) =>
            handleChange({ grantType: e.target.value as OAuth2Config['grantType'] })
          }
          className="w-full px-3 py-2 bg-background border border-input rounded-md"
        >
          <option value="client_credentials">Client Credentials</option>
          <option value="password">Password</option>
          <option value="authorization_code">Authorization Code</option>
        </select>
      </div>

      {/* Access Token URL */}
      <div>
        <label className="block text-sm font-medium mb-1">Access Token URL</label>
        <input
          type="text"
          value={config.accessTokenUrl || ''}
          onChange={(e) => handleChange({ accessTokenUrl: e.target.value })}
          placeholder="https://auth.example.com/oauth/token"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Client ID */}
      <div>
        <label className="block text-sm font-medium mb-1">Client ID</label>
        <input
          type="text"
          value={config.clientId || ''}
          onChange={(e) => handleChange({ clientId: e.target.value })}
          placeholder="your-client-id"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Client Secret */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Client Secret <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          type="password"
          value={config.clientSecret || ''}
          onChange={(e) => handleChange({ clientSecret: e.target.value })}
          placeholder="your-client-secret"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Scope */}
      <div>
        <label className="block text-sm font-medium mb-1">
          Scope <span className="text-muted-foreground">(optional)</span>
        </label>
        <input
          type="text"
          value={config.scope || ''}
          onChange={(e) => handleChange({ scope: e.target.value })}
          placeholder="read write"
          className="w-full px-3 py-2 bg-background border border-input rounded-md font-mono text-sm"
        />
      </div>

      {/* Password Grant Fields */}
      {config.grantType === 'password' && (
        <>
          <div>
            <label className="block text-sm font-medium mb-1">Username</label>
            <input
              type="text"
              value={config.username || ''}
              onChange={(e) => handleChange({ username: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-input rounded-md"
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-1">Password</label>
            <input
              type="password"
              value={config.password || ''}
              onChange={(e) => handleChange({ password: e.target.value })}
              className="w-full px-3 py-2 bg-background border border-input rounded-md"
            />
          </div>
        </>
      )}

      {/* Get Token Button */}
      <div className="flex items-center gap-3">
        <button
          onClick={handleGetToken}
          disabled={isLoading || !config.accessTokenUrl || !config.clientId}
          className="px-4 py-2 bg-primary text-primary-foreground rounded-md hover:bg-primary/90 disabled:opacity-50 flex items-center gap-2"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : hasValidToken ? (
            <RefreshCw className="w-4 h-4" />
          ) : null}
          {hasValidToken ? 'Refresh Token' : 'Get New Token'}
        </button>

        {hasValidToken && (
          <span className="flex items-center gap-1 text-sm text-green-600">
            <Check className="w-4 h-4" />
            Token acquired
          </span>
        )}

        {isTokenExpired && (
          <span className="flex items-center gap-1 text-sm text-orange-500">
            <AlertCircle className="w-4 h-4" />
            Token expired
          </span>
        )}
      </div>

      {error && (
        <div className="text-sm text-destructive flex items-center gap-2">
          <AlertCircle className="w-4 h-4" />
          {error}
        </div>
      )}

      {/* Current Token Display */}
      {config.accessToken && (
        <div>
          <label className="block text-sm font-medium mb-1">Access Token</label>
          <input
            type="text"
            value={config.accessToken}
            readOnly
            className="w-full px-3 py-2 bg-muted border border-input rounded-md font-mono text-sm text-muted-foreground"
          />
        </div>
      )}
    </div>
  );
}

export default OAuth2Auth;
